import React, { useState } from 'react';
import { Grid, Button, Tabs, Tab, Box } from '@mui/material';

import { VHPDataPublisher, VHPDataRetriever, ComeFrom } from '@care/mfe-data-transfer-vhp';
import { Location } from '@/types';
import useEnterKey from '@/components/hooks/useEnterKey';
import useTestFlags from '@/components/hooks/useTestFlags';
import { isValidZipCode } from '@/utilities/zipCodeValidators';
import ZipInput from './ZipInput';

interface ZipCodePanelProps {
  onZipCodeSelected: (location: Location | undefined, isProvider: boolean) => void;
  panelCtaText?: string;
  iconLeft?: boolean;
}

function ZipCodePanel(props: ZipCodePanelProps) {
  const { onZipCodeSelected, panelCtaText = 'Search', iconLeft = false } = props;

  const { greaterCare } = useTestFlags();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [enterKeyEnabled, setEnterKeyEnabled] = useState<boolean>(false);
  const [locationEntered, setLocationEntered] = useState<Location | undefined>(undefined);
  const isGreaterCareEnabled = greaterCare?.value === 1;

  const locationWrappedOnZipCodeSelected = () => {
    const VHPRetriever = new VHPDataRetriever();
    const existingData = VHPRetriever.retrieveData();
    const data = {
      ...existingData,
      comeFrom: ComeFrom.VHP,
    };
    const VHPPublisher = new VHPDataPublisher();

    VHPPublisher.publishData(data);
    onZipCodeSelected(locationEntered, selectedTab === 1);
  };

  const handleZipCodeFieldChange = (location: Location) => {
    if (
      location.verifiedApiZipcode &&
      location.city &&
      location.state &&
      isValidZipCode(location.verifiedApiZipcode)
    ) {
      setLocationEntered(location);
      const VHPRetriever = new VHPDataRetriever();
      const existingData = VHPRetriever.retrieveData();
      const data = {
        ...existingData,
        zipcodeData: {
          zipcode: location.verifiedApiZipcode,
          city: location.city,
          state: location.state,
        },
        comeFrom: ComeFrom.VHP,
      };
      const VHPPublisher = new VHPDataPublisher();

      VHPPublisher.publishData(data);
    }
    if (location.inputZipcode) {
      setEnterKeyEnabled(location.inputZipcode.length > 0);
    } else {
      setEnterKeyEnabled(false);
    }
  };

  const handleTabStateChange = (_: object, value: any) => {
    setSelectedTab(parseInt(value, 10));
  };

  useEnterKey(enterKeyEnabled, () => locationWrappedOnZipCodeSelected());

  return (
    <form>
      <Box
        sx={{
          backgroundColor: 'transparent',
          border: 'transparent 1px solid',
        }}>
        <Box
          sx={(theme) => ({
            '& .MuiTab-root': {
              borderBottomStyle: 'none',
              padding: 0,
              marginRight: 4,
              fontSize: {
                xs: '18px',
                sm: '21px',
              },
              fontWeight: 500,
              minWidth: 'auto',
              width: 'auto',

              color: isGreaterCareEnabled
                ? theme.palette.text.contrast
                : theme.palette.text.primary,
              '&[aria-selected="true"]': {
                color: theme.palette.brand.accent,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.brand.accent,
            },
            margin: {
              xs: theme.spacing(0, 0, 2),
            },
          })}>
          <Tabs value={selectedTab} onChange={handleTabStateChange}>
            <Tab label="Find care" disableRipple />
            <Tab label={isGreaterCareEnabled ? 'Find jobs' : 'Find a job'} disableRipple />
          </Tabs>
        </Box>

        <Grid direction="column" container>
          <Box>
            <ZipInput
              onChange={handleZipCodeFieldChange}
              validateOnLostFocusOrClick
              validateOnClick={false}
              withIntegratedCta={false}
              handleZipCodeButtonClick={locationWrappedOnZipCodeSelected}
              showLocationIcon
              iconLeft={iconLeft}
              sx={[
                {
                  ...(isGreaterCareEnabled && {
                    '& .MuiInputBase-root': {
                      borderColor: 'transparent',
                    },
                  }),
                },
              ]}
            />
          </Box>
          <Box pt={3}>
            <Button
              fullWidth
              color={isGreaterCareEnabled ? 'secondary' : 'primary'}
              variant="contained"
              size="large"
              onClick={locationWrappedOnZipCodeSelected}>
              {panelCtaText}
            </Button>
          </Box>
        </Grid>
      </Box>
    </form>
  );
}

export default ZipCodePanel;
